'use client'

import GradientText from './GradientText'
import { useInView } from '../hooks/useInView'

export default function Features() {
  const features = [
    {
      image: "/images/features_1.webp",
      icon: (
        <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      title: "Model Any GTM Scenario with the Business Canvas",
      description: "Our AI builds a comprehensive strategic overview of any market you define. It analyzes market size (TAM, SAM, SOM), identifies ideal customer personas, dissects competitors, and defines your core value proposition to give you a 360° view of the battlefield."
    },
    {
      image: "/images/features_2.webp",
      icon: (
        <svg className="w-8 h-8 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: "Simulate and De-Risk Your Marketing Spend",
      description: "Generate multiple, hyper-detailed performance marketing simulations. Each \"Winning Game Plan\" is a complete, launch-ready brief for channels like Google Ads, detailing budgets, keywords, ad copy, and forecasting your expected CTR, CPC, and conversions."
    }
  ]

  const { ref: sectionRef, inView: cardsInView } = useInView({ 
    threshold: 0.2, 
    rootMargin: '-50px' 
  })
  
  const { ref: headerRef, inView: headerInView } = useInView({ 
    threshold: 0.1, 
    rootMargin: '0px' 
  })

  return (
    <section id="features" className="relative min-h-screen overflow-hidden py-12 lg:py-16" style={{ backgroundColor: '#001223' }}>
      {/* Content */}
      <div className="relative z-10">
        <div className="container-stripe">
        {/* Section Header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-20 px-4 sm:px-0">
          <div className={`glassmorphism-button group inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 text-white text-xs font-medium mb-6 sm:mb-8 cursor-default transition-all duration-700 ease-out ${
            headerInView 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-4'
          }`}>
            THE PLATFORM
          </div>
          <h2 className={`text-3xl sm:text-4xl md:text-6xl font-light mb-4 text-balance text-center max-w-5xl mx-auto text-white leading-relaxed drop-shadow-lg tracking-tight transition-all duration-700 ease-out ${
            headerInView 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: headerInView ? '100ms' : '0ms' }}>
            Foresight is Your{' '}
            <GradientText colors={["#ffffff", "#bfdbfe", "#60a5fa", "#bfdbfe", "#ffffff"]}>
              New Superpower
            </GradientText>
          </h2>
          <p className={`text-sm sm:text-base md:text-lg font-light mb-8 sm:mb-12 max-w-3xl mx-auto text-center text-blue-100/95 leading-relaxed drop-shadow-sm transition-all duration-700 ease-out ${
            headerInView 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-6'
          }`}
          style={{ transitionDelay: headerInView ? '200ms' : '0ms' }}>
            Our platform is built on two core pillars: deep strategic analysis and actionable, simulated execution.
          </p>
        </div>

        {/* Features Grid */}
        <div ref={sectionRef} className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-20 max-w-4xl mx-auto relative px-4 sm:px-0">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group aspect-[4/5] sm:aspect-[3/4] flex flex-col relative overflow-hidden rounded-stripe-lg border border-gray-900 transition-all duration-700 ease-out ${
                cardsInView 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ 
                transitionDelay: cardsInView ? `${index * 200}ms` : '0ms',
                boxShadow: 'inset 0 4px 16px rgba(0, 0, 0, 0.3)',
                backgroundImage: `url(${feature.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/75 to-black/90 rounded-stripe-lg transition-all duration-300"></div>
              
              {/* Content */}
              <div className="flex flex-col justify-end h-full relative z-10 p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl md:text-2xl font-normal mb-3 sm:mb-4 text-white leading-tight tracking-tight">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base md:text-lg font-light text-blue-100/95 leading-relaxed drop-shadow-sm">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
        </div>
      </div>
    </section>
  )
}